import { useMemo } from 'react';
import {
  cn,
  Flex,
  Icon,
  ShimmeringText,
  Text,
  type IIconProps,
} from '@kognitos/lattice';

import type {
  V1Phase,
  V1Plan,
  V1Step,
  V1StepStatus,
} from '@/generated/hooks/rain/models';

/** `"<phaseIdx>:<stepIdx>"` of the first unresolved step across the active plan
 *  — the "current" step — or null when the plan is inactive or fully resolved. */
function currentStepKey(plan: V1Plan): string | null {
  if (!plan.active) return null;
  const phases = plan.phases ?? [];
  for (let pi = 0; pi < phases.length; pi += 1) {
    const steps = phases[pi].steps ?? [];
    for (let si = 0; si < steps.length; si += 1) {
      const s = steps[si].status;
      if (
        !s ||
        s === 'STEP_STATUS_PENDING' ||
        s === 'STEP_STATUS_UNSPECIFIED'
      ) {
        return `${pi}:${si}`;
      }
    }
  }
  return null;
}

function phaseProgress(phase: V1Phase): { total: number; completed: number } {
  const steps = phase.steps ?? [];
  let completed = 0;
  for (const s of steps) {
    if (
      s.status === 'STEP_STATUS_COMPLETED' ||
      s.status === 'STEP_STATUS_SKIPPED'
    ) {
      completed += 1;
    }
  }
  return { total: steps.length, completed };
}

export function getPlanProgress(plan: V1Plan | undefined): {
  total: number;
  completed: number;
  failed: number;
} {
  if (!plan?.phases) return { total: 0, completed: 0, failed: 0 };
  let total = 0;
  let completed = 0;
  let failed = 0;
  for (const phase of plan.phases) {
    const { total: t, completed: c } = phaseProgress(phase);
    total += t;
    completed += c;
    for (const step of phase.steps ?? []) {
      if (step.status === 'STEP_STATUS_FAILED') failed += 1;
    }
  }
  return { total, completed, failed };
}

interface IStepIconDescriptor {
  type: IIconProps['type'];
  className: string;
}

function getStatusIconType(
  status: V1StepStatus | undefined,
  isCurrent: boolean,
): IStepIconDescriptor {
  if (status === 'STEP_STATUS_COMPLETED') {
    return { type: 'CircleCheck', className: 'text-success' };
  }
  if (status === 'STEP_STATUS_FAILED') {
    return { type: 'CircleX', className: 'text-destructive' };
  }
  if (isCurrent) {
    return { type: 'CircleArrowRight', className: 'text-muted-foreground' };
  }
  return { type: 'CircleDashed', className: 'text-muted-foreground' };
}

interface IPlanStepRowProps {
  step: V1Step;
  isCurrent: boolean;
}

function PlanStepRow({ step, isCurrent }: IPlanStepRowProps) {
  const status = step.status ?? 'STEP_STATUS_PENDING';
  const isFailed = status === 'STEP_STATUS_FAILED';
  const isStruck =
    status === 'STEP_STATUS_COMPLETED' || status === 'STEP_STATUS_SKIPPED';
  const icon = getStatusIconType(status, isCurrent);
  const name = step.name ?? '';

  return (
    <Flex
      align="center"
      gap={2}
      className="min-w-0 w-full"
      data-testid="rain-plan-step"
      data-current={isCurrent || undefined}
    >
      <Icon
        type={icon.type}
        size={18}
        className={cn('shrink-0', icon.className)}
      />
      <Flex direction="column" className="min-w-0 flex-1">
        {isCurrent ? (
          <ShimmeringText
            text={name}
            duration={2}
            className="block max-w-full truncate text-sm font-medium"
            data-testid="rain-plan-step-current"
          />
        ) : (
          <Text
            as="span"
            level="small"
            color={isFailed ? 'destructive' : 'muted'}
            className={cn('block truncate', isStruck && 'line-through')}
          >
            {name}
          </Text>
        )}
        {isFailed && step.failure_reason && (
          <Text level="xSmall" color="destructive" className="mt-0.5">
            {step.failure_reason}
          </Text>
        )}
      </Flex>
    </Flex>
  );
}

interface IPlanPhaseProps {
  phase: V1Phase;
  phaseIndex: number;
  currentKey: string | null;
  showLabel: boolean;
}

function PlanPhase({
  phase,
  phaseIndex,
  currentKey,
  showLabel,
}: IPlanPhaseProps) {
  const steps = phase.steps ?? [];
  return (
    <Flex direction="column" className="w-full">
      {showLabel && (
        <Text
          as="span"
          level="small"
          color="muted"
          className={cn(
            'truncate pb-1',
            phaseIndex === 0 ? 'pt-0.5' : 'pt-2.5',
          )}
        >
          {phase.name}
        </Text>
      )}
      <Flex direction="column" gap={2} className="w-full py-1">
        {steps.map((step, si) => (
          <PlanStepRow
            key={`${si}-${step.name ?? ''}`}
            step={step}
            isCurrent={currentKey === `${phaseIndex}:${si}`}
          />
        ))}
      </Flex>
    </Flex>
  );
}

interface IRainPlanViewProps {
  plan: V1Plan;
  /** A retired plan keeps `active: true`, so nothing marks itself current. */
  isHistorical?: boolean;
}

/**
 * The plan's steps as a flat checklist. Phase names only appear when there is
 * more than one phase, since a lone phase name just repeats the card title.
 *
 * @param props - Component props
 * @param props.plan - Plan to render
 * @param props.isHistorical - Suppresses the current-step marker
 * @returns The step list
 */
export function RainPlanView({ plan, isHistorical }: IRainPlanViewProps) {
  const phases = plan.phases ?? [];
  const currentKey = useMemo(
    () => (isHistorical ? null : currentStepKey(plan)),
    [isHistorical, plan],
  );
  const hasMultipleGroups = phases.length > 1;

  return (
    <Flex direction="column" className="w-full">
      {phases.map((phase, pi) => (
        <PlanPhase
          key={`${pi}-${phase.name ?? ''}`}
          phase={phase}
          phaseIndex={pi}
          currentKey={currentKey}
          showLabel={hasMultipleGroups}
        />
      ))}
    </Flex>
  );
}
